<template>

  <MainSiteLayout
    class="ArticleLayout"
    :content="{ brand, pencilBanner: pageContent.pencilBanner || pageContent.defaultPencilBanner, mainSiteNav: pageContent.mainSiteNav }"
  >

    <Container class="mainContent">

      <BackLink v-if="backLink" v-bind="backLink" />

      <WithSidebar class="articleMain">

        <slot name="header"></slot>

        <SocialIcons class="headerSocialIcons" />

        <slot name="body" v-bind="{ pageCta }"></slot>

        <SocialIcons class="footerSocialIcons" />
        <RelevantPages v-if="settingsContent.relevantPages && settingsContent.relevantPages.length > 0"
          :items="settingsContent.relevantPages"
          class="relevantPages"
        />
        <VisualCta v-if="settingsContent.visualCta" :content="settingsContent.visualCta" class="visualCta" />

        <template v-slot:sidebar>
          <div class="sidebarContent">
            <SidebarRelatedItems v-if="relatedItemsContent" v-bind="relatedItemsContent" />
            <PageCta v-if="pageCta" v-bind="pageCta" class="--sidebar sidebarCta" />
          </div>
        </template>

      </WithSidebar>

      <div class="footerArea" v-if="showFooterRelatedItems">
        <FooterRelatedItems v-if="relatedItemsContent"
          :heading="localeStrings.youMightBeInterestedInThese"
          :resourceLinks="relatedItemsContent.resourceLinks"
          :viewMoreLink="relatedItemsContent.viewMoreLink"
        />
      </div>

    </Container>

    <FooterCta v-if="pageContent.footerCta" :content="pageContent.footerCta" />

    <GatedContentDownloadModuleDialog v-if="pageCta?.content?.popoverItem?._type === 'gatedContentDownloadModule'"
      ref="gatedContentDownloadDialog"
      :content="pageCta.content.popoverItem"
    />

    <QuizModuleDialog v-if="pageCta?.content?.popoverItem?._type === 'quizModule'"
      ref="quizModuleDialog"
      :content="pageCta.content.popoverItem"
    />

  </MainSiteLayout>

</template>

<script>
import MainSiteLayout from '../../layouts/MainSiteLayout.vue'

import ArticleBodyContent from './ArticleBodyContent.vue'
import BackLink from './BackLink.vue'
import FooterCta from '../shared/FooterCta.vue'
import FooterRelatedItems from './FooterRelatedItems.vue'
import GatedContentDownloadModuleDialog from './GatedContentDownloadModule/Dialog.vue'
import PageCta from './PageCta.vue'
import QuizModuleDialog from './QuizModuleDialog.vue'
import RelevantPages from './RelevantPages.vue'
import SidebarRelatedItems from './SidebarRelatedItems.vue'
import SocialIcons from './SocialIcons.vue'
import VisualCta from './VisualCta.vue'
import WithSidebar from '../shared/WithSidebar.vue'

const pageCtaQuery = `
  pageCta {
    label,
    title,
    image,
    ctaText,
    ctaAction,
    popoverItem->,
    urlOrPath,
  }
`

export const extraContentQueryFragment = `
  footerCta->{ title, text, cta },
  ${pageCtaQuery},
  "settings": *[_type == 'resourcesSettings' && locale == ^.locale][0] {
    segment5g { ${pageCtaQuery}, relevantPages, visualCta },
    segmentFleet { ${pageCtaQuery}, relevantPages, visualCta },
  }
`

const segmentValues = {
  '5g': { brand: 'inseego', settingsKey: 'segment5g' },
  'fleet': { brand: 'inseegoFleet', settingsKey: 'segmentFleet' },
}

export default {
  components: {
    ArticleBodyContent,
    BackLink,
    FooterCta,
    FooterRelatedItems,
    GatedContentDownloadModuleDialog,
    MainSiteLayout,
    PageCta,
    QuizModuleDialog,
    RelevantPages,
    SidebarRelatedItems,
    SocialIcons,
    VisualCta,
    WithSidebar,
  },
  props: {
    segment: {
      type: String,
      required: true,
    },
    backLink: {
      type: Object,
      required: false,
    },
    showFooterRelatedItems: {
      type: Boolean,
      default: false,
    },
    pageContent: {
      type: Object,
      required: false,
    },
    relatedItemsContent: {
      type: Object,
      required: false,
    },
  },
  inject: ['localeStrings'],
  computed: {
    brand() {
      return segmentValues[this.segment].brand
    },
    settingsContent() {
      const segmentFieldName = segmentValues[this.segment].settingsKey
      return this.pageContent.settings[segmentFieldName] ?? {}
    },
    pageCta() {
      return {
        content: this.pageContent?.pageCta || this.settingsContent?.pageCta,
        showPopover: () => {
          const dialog = (
            this.$refs.gatedContentDownloadDialog
            || this.$refs.quizModuleDialog
          )
          if (dialog) {
            dialog.showModal()
          }
        },
      }
    },
  },
}
</script>

<style scoped>
.mainContent {
  border-top: thin solid var(--hairline-color);
  padding-top: var(--spacing-11);
  padding-bottom: var(--spacing-17);
}

.headerSocialIcons {
  margin-top: var(--spacing-8);
}

.articleMain {
  margin-top: var(--spacing-10);
}

.footerSocialIcons {
  margin-top: var(--spacing-10);
}

.relevantPages {
  margin-top: var(--spacing-15);
}

.visualCta {
  margin-top: var(--spacing-15);
}

.sidebarContent {
  display: none;
}

.sidebarCta {
  margin-top: var(--spacing-8);
}

.footerArea {
  margin-top: var(--spacing-12);
  border-top: 2px solid var(--hairline-color);
  padding-top: var(--spacing-10);
}

@media (--screen-lg) {

  .sidebarContent {
    display: block;
    position: sticky;
    top: var(--spacing-5);
  }
}
</style>
